import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { AddNewGroupComponent } from "../../add-new-group/add-new-group.component";
import { NavBarComponent } from "../../../nav-bar/nav-bar.component";
import { SampleService } from "../../../services/sample.service";
import { catchError, takeUntil } from "rxjs/operators";
import { ReplaySubject, Subject, throwError } from "rxjs";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { GroupService } from 'src/app/services/group.service';
import { SampleGroupModel } from 'src/app/models/sample-group-model';
import { HelperService } from 'src/app/helper.service';
import { PinDetailsService } from 'src/app/pin-reworks/pin-services/pin-details.service';
import { is } from 'node_modules/cypress/types/bluebird';
import { sampleDecimalText, sampleDecimalValidator, sampleDecimalValue } from 'src/app/utils/sample-decimal';


@Component({
    selector: 'app-add-edit-sample',
    templateUrl: './add-edit-soil-sample.component.html',
    styleUrls: ['./add-edit-soil-sample.component.css'],
    standalone: false
})
export class AddEditSoilSampleComponent implements OnInit, OnDestroy {
  soilSampleForm: FormGroup;
  groups = [];
  group: SampleGroupModel = null;
  userId = this.data.userId;
  tenantId = this.data.tenantId;
  pinId: number = this.data.pinId;
  sampleId: number = this.data.sampleId;

  isEditSampleMode: boolean = false;
  isAddNewSapleMode: boolean = false;

  moveToGroupId: number = 0;
  selectedToMove = null;
  isCompleted: boolean = false;
  definitions: any[] = [];
  selectedDefinitionId: number = null;
  isOrphanedLink: boolean = false;

  /** control for the selected group */
  // public groupCtrl: FormControl<SampleGroupModel> = new FormControl<SampleGroupModel>(null);

  /** control for the MatSelect filter keyword */
  public groupFilterCtrl: FormControl<string> = new FormControl<string>('');

  /** list of group  filtered by search keyword */
  public filteredSampleGroups: ReplaySubject<SampleGroupModel[]> = new ReplaySubject<SampleGroupModel[]>(1);

  /** Subject that emits when the component has been destroyed. */
  protected _onGroupDestroy = new Subject<void>();


  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
    private newSampleFb: FormBuilder,
    public soilSampleDialog: MatDialogRef<AddEditSoilSampleComponent>,
    public addGroupDialog: MatDialog,
    private sampleService: SampleService,
    private groupService: GroupService,
    public nav: NavBarComponent,
    public moveToDialog: MatDialog,
    public helper: HelperService,
    private pinDetailsService: PinDetailsService
  ) {
    this.soilSampleForm = this.newSampleFb.group({
      sampleName: ['', Validators.required],
      bagNumber: new FormControl(''),
      // Validated rather than typed as a number input: see utils/sample-decimal.ts. A browser
      // on South African regional settings renders and parses type="number" with a comma, so a
      // typed full stop was discarded and the field saved empty.
      sampleDepth: new FormControl(null, sampleDecimalValidator),
      rockPercentage: new FormControl(null, sampleDecimalValidator),
      note: new FormControl(''),
      pinSampleGroup: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    this.nav.toggleLoading(true);

    // The mode is read first, before anything that depends on it.
    //
    // setPreviousSelection() used to run above this, so its !isEditSampleMode guard read the
    // field default instead of the dialog's data and never guarded anything: opening a sample
    // to edit filled the form from the PREVIOUS sample and only corrected itself a second
    // later. The same ordering left populateGroupDropdown's isEditSampleMode filter always
    // false, which dropped a grid group from the dropdown when editing a sample inside one.
    if (this.data !== null) {
      this.isEditSampleMode = this.data.isEditSampleMode;
      this.isAddNewSapleMode = this.data.isAddNewSapleMode;
    }

    if (this.isAddNewSapleMode && this.data?.isGrid && this.data?.gridSampleName) {
      this.soilSampleForm.controls.sampleName.setValue(this.data.gridSampleName);
    }

    // Which group to select once the list arrives: the sample's own when editing, the grid's
    // for a grid sample, otherwise whatever the last captured sample used.
    let groupToSelect: number = null;

    if (this.isEditSampleMode) {
      this.populateEditValues();
      groupToSelect = this.data?.linkedGroup ?? null;
    } else {
      const carriedGroup = this.setPreviousSelection();
      groupToSelect = this.data?.isGrid ? (this.data?.linkedGroup ?? null) : carriedGroup;
    }

    this.populateGroupDropdown(groupToSelect);
    this.loadDefinitions();
  }

  /**
   * The sample being edited, written straight into the form.
   *
   * This used to sit inside a 1000ms setTimeout that was waiting for the group list. When
   * nothing had been captured yet in the session the loading overlay came down immediately,
   * leaving the dialog blank and clickable for that second with Update enabled, so a quick
   * click saved the blanks over the sample. The group is now chosen by populateGroupDropdown
   * when the list actually arrives, so none of this has to wait for anything.
   */
  private populateEditValues(): void {
    this.isCompleted = this.data?.isCompleted;
    this.soilSampleForm.controls.sampleName.setValue(this.data.sampleName);
    this.soilSampleForm.controls.bagNumber.setValue(this.data.bagNumber ?? '');
    this.soilSampleForm.controls.sampleDepth.setValue(sampleDecimalText(this.data.sampleDepth));
    this.soilSampleForm.controls.rockPercentage.setValue(sampleDecimalText(this.data.rockPercentage));
    this.soilSampleForm.controls.note.setValue(this.data.note);
  }

  /**
* Method that sets the intial group list that will be displayed in the group dropdown.
*/
  setInitialGroupList() {
    // Load the initial group list
    this.filteredSampleGroups.next(this.groups.slice());
    // Listen for search field value changes
    this.groupFilterCtrl.valueChanges
      .pipe(takeUntil(this._onGroupDestroy))
      .subscribe(() => {
        this.filterGroups();
      });
  }


  protected filterGroups() {
    if (!this.groups) {
      return;
    }
    // Get the search keyword
    let search = this.groupFilterCtrl.value;
    if (!search) {
      this.filteredSampleGroups.next(this.groups.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    // Filter the group based on the search keyword
    this.filteredSampleGroups.next(
      this.groups.filter(group => group.name.toLowerCase().indexOf(search) > -1)
    );
  }


  addNewGroup() {
    const dialogRef = this.addGroupDialog.open(AddNewGroupComponent, {
      data: {
        userId: this.userId,
        tenantId: this.tenantId,
      },
      width: '420px'
    });

    dialogRef.afterClosed().subscribe(groupId => {
      if(groupId) {
        this.populateGroupDropdown(groupId);
      }
    });
  }

  populateGroupDropdown(groupId?: number) {
    this.groupService.getGroups(this.data.tenantId).pipe(
      catchError(error => {
        console.error();
        return throwError(() => error);
      })
    ).subscribe({
      next: (result) => {
        this.groups = result;
        if(this.groups.length > 0) {
          this.groups = this.groups.filter(g => !g.isGrid || this.data?.isGrid || (this.isEditSampleMode && this.data?.linkedGroup === g.id));
        }

        // Selected here, where the list is known to exist, rather than on a timer that was
        // racing this very call. Only when it is actually found: a reload that misses must not
        // wipe a selection the user has already made.
        if(groupId) {
          const group = this.groups.find(g => g.id === groupId);
          if (group) {
            this.group = group;
            this.soilSampleForm.controls.pinSampleGroup.setValue(group);
          }
        }

        this.setInitialGroupList();
        this.nav.toggleLoading(false);
      },
      // Without this the full screen overlay never comes down and the dialog cannot be used
      // or closed.
      error: () => this.nav.toggleLoading(false)
    })
  }


  saveSample() {
    this.nav.toggleLoading(true);

    let sampleObj = {
      pinId: this.pinId,
      linkedGroup: this.soilSampleForm.get('pinSampleGroup').value?.id,
      name: this.soilSampleForm.get('sampleName').value,
      // Numbers, never the typed text: these land in numeric columns that reject "2,5". The
      // control validator has already refused anything that is not a number, so nothing is
      // silently dropped here.
      sampleDepth: sampleDecimalValue(this.soilSampleForm.get('sampleDepth').value),
      rockPercentage: sampleDecimalValue(this.soilSampleForm.get('rockPercentage').value),
      note: this.soilSampleForm.get('note').value,
      bagNumber: this.soilSampleForm.get('bagNumber').value
    };

    this.pinDetailsService.setSoilSampleDetails(sampleObj);

    this.sampleService.postSoilSample({
      pinId: sampleObj?.pinId,
      pinSampleGroupId: sampleObj?.linkedGroup,
      name: sampleObj?.name,
      sampleDepth: sampleObj?.sampleDepth,
      rockPercentage: sampleObj?.rockPercentage,
      note: sampleObj?.note,
      bagNumber: sampleObj?.bagNumber,
      seasonFieldDetailId: this.selectedDefinitionId
    }).pipe(
      catchError(error => {
        console.error(error);
        this.nav.toggleLoading(false);
        return throwError(() => error);
      })
    ).subscribe(() => {
      this.soilSampleDialog.close();
    });
  }

  deleteSample() {

    this.nav.toggleLoading(true);

    this.sampleService.deleteSampleById(this.sampleId).pipe(
      catchError(error => {
        console.error(error);
        this.nav.toggleLoading(false);
        return throwError(() => error);
      })
    ).subscribe(() => {
      this.soilSampleDialog.close();
      this.nav.toggleLoading(false);
    });
  }

  updateSample() {
    this.nav.toggleLoading(true);

      let sampleObj = {
        pinId: this.pinId,
        sampleId: this.sampleId,
        linkedGroup: this.moveToGroupId ? this.moveToGroupId : this.soilSampleForm.get('pinSampleGroup').value?.id,
        name: this.soilSampleForm.get('sampleName').value,
        sampleDepth: sampleDecimalValue(this.soilSampleForm.get('sampleDepth').value),
        rockPercentage: sampleDecimalValue(this.soilSampleForm.get('rockPercentage').value),
        note: this.soilSampleForm.get('note').value,
        bagNumber: this.soilSampleForm.get('bagNumber').value,
        is_completed: this.isCompleted
      };
      this.sampleService.updateSoilSample(sampleObj?.sampleId, {
        pinId: sampleObj?.pinId,
        pinSampleGroupId: sampleObj?.linkedGroup,
        name: sampleObj?.name,
        sampleDepth: sampleObj?.sampleDepth,
        rockPercentage: sampleObj?.rockPercentage,
        note: sampleObj?.note,
        bagNumber: sampleObj?.bagNumber,
        isCompleted: sampleObj?.is_completed
      }).pipe(
        catchError(error => {
          console.error(error);
          this.nav.toggleLoading(false);
          return throwError(() => error);
        })
      ).subscribe(() => {
        if (this.selectedDefinitionId) {
          this.sampleService.updateSampleLink(this.sampleId, this.selectedDefinitionId).subscribe(() => {
            this.soilSampleDialog.close();
          });
        } else {
          this.soilSampleDialog.close();
        }
      });
  }


  updateSampleGroup(group: SampleGroupModel) {
    if (this.group?.id !== group?.id) {
      this.moveToGroupId = group?.id;
    }
  }

  cancel() {
    this.soilSampleDialog.close();
  }


  /**
   * Carries the last captured sample forward, so an agent filling a pin does not retype the
   * same depth and rock percentage for every bag.
   *
   * Returns the group to select, which the caller hands to populateGroupDropdown. It used to
   * search this.groups itself inside a 1000ms setTimeout, racing the very call that fills that
   * list: on a slow connection the list was still empty and the group was quietly not restored.
   *
   * Only ever called for a new sample. The caller decides that now; the guard that used to be
   * here read isEditSampleMode before it had been assigned.
   */
  setPreviousSelection(): number {
    const previousSelectedSoilDetail = this.pinDetailsService.getSoilSampleDetails();
    if (!previousSelectedSoilDetail) {
      return null;
    }

    // Name not restored for grid samples - they get auto-generated names
    if (!this.data?.isGrid) {
      this.soilSampleForm.controls.sampleName.setValue(previousSelectedSoilDetail?.name);
    }
    this.soilSampleForm.controls.sampleDepth.setValue(sampleDecimalText(previousSelectedSoilDetail?.sampleDepth));
    this.soilSampleForm.controls.rockPercentage.setValue(sampleDecimalText(previousSelectedSoilDetail?.rockPercentage));
    this.soilSampleForm.controls.note.setValue(previousSelectedSoilDetail?.note);
    // Carried forward like every other field on this form. Decided 18/10/2026, during a live
    // soil lab run: the typing saved across a bench of samples is worth more than the duplicate
    // it leaves behind. Two samples sharing a bag number sort as equal and fall through to farm,
    // field and name, so the agent is expected to change it on each new sample.
    this.soilSampleForm.controls.bagNumber.setValue(previousSelectedSoilDetail?.bagNumber ?? '');

    return this.data?.isGrid ? null : (previousSelectedSoilDetail?.linkedGroup ?? null);
  }


  loadDefinitions() {
    this.sampleService.getDefinitionsForPin(this.pinId).subscribe((defs) => {
      this.definitions = defs || [];
      if (this.data?.seasonFieldDetailId) {
        this.selectedDefinitionId = this.data.seasonFieldDetailId;
        this.isOrphanedLink = !this.definitions.some(d => d.id === this.selectedDefinitionId);
      } else if (this.definitions.length === 1) {
        this.selectedDefinitionId = this.definitions[0].id;
      }
    });
  }

  ngOnDestroy() {
    this._onGroupDestroy.next();
    this._onGroupDestroy.complete();
  }
}
